import React, { Component } from "react";
import {
  Modal,
  Input,
  Button,
  Typography,
  Card,
  Row,
  Col,
  Divider,
} from "antd";
import {
  CheckCircleOutlined,
  CarOutlined,
  UserOutlined,
  CalendarOutlined,
  SolutionOutlined,
  EnvironmentOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-routing-machine";
import "leaflet-routing-machine/dist/leaflet-routing-machine.css";
import "./GargashPageTracker.css";

// Fix for default marker icons
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

const { Title, Text } = Typography;

interface Location {
  lat: number;
  lng: number;
  address: string;
}

interface OrderDetails {
  deliveryPartnerStatus: string;
  deliveryPartnerId: string;
  serviceType: string;
  orderId: string;
  customerName: string;
  driverName: string;
  driverNumber: string;
  plateNumber: string;
  chassisNumber: string;
  vehicle: string;
  serviceDate: string;
  status: string;
  amount: number;
  pickupLocation: Location; // This is the 'delivery_location' from your API
  dropLocation: Location; // This is the 'recollection_location' from your API
  driverLocation?: {
    lat: number;
    lng: number;
  };
}

interface DeliveryLocation {
  delivery_partner_id: string;
  order_number: string;
  latitude: string;
  longitude: string;
  order_status: string;
  delivery_partner_status: string;
  changed_at: string;
  is_jafza_service: boolean;
  location: string;
  distance_in_km: number;
  total_break_time: number;
}

interface GargashPageTrackerState {
  showOtpModal: boolean;
  otp: string;
  isVerified: boolean;
  orderDetails: OrderDetails | null;
  error?: string;
  isLoading: boolean;
  mapInitialized: boolean;
  deliveryLocations: DeliveryLocation[] | null;
  driverLocationAddress: string;
}

// Extend the Leaflet type definitions to include the remove method
declare module "leaflet-routing-machine" {
  namespace L {
    namespace Routing {
      interface Control {
        remove(): this;
      }
    }
  }
}

export class GargashPageTracker extends Component<{}, GargashPageTrackerState> {
  private mapRef = React.createRef<HTMLDivElement>();
  private map: L.Map | null = null;
  private routingControl: L.Routing.Control | null = null;

  // Format date in dd-mm-yyyy hh:mm format
  private formatDate(dateString: string): string {
    if (!dateString || dateString === "N/A") return dateString;

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid date

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${day}-${month}-${year} ${hours}:${minutes}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  }

  private async getAddressFromCoordinates(
    lat: number,
    lng: number
  ): Promise<string> {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=en`,
        {
          headers: {
            "User-Agent": "CarHireMerchantApp/1.0 (contact@example.com)",
          },
        }
      );
      const data = await response.json();
      return data.display_name || "Address not found";
    } catch (error) {
      console.error("Error fetching address:", error);
      return "Address not found";
    }
  }

  constructor(props: {}) {
    super(props);
    this.state = {
      showOtpModal: true,
      otp: "",
      isVerified: false,
      orderDetails: null,
      isLoading: false,
      mapInitialized: false,
      deliveryLocations: null,
      driverLocationAddress: "Loading address...",
    };
  }

  fetchAuthToken = async () => {
    // Ensure environment variables are loaded
    if (
      !process.env.REACT_APP_CLIENT_URL ||
      !process.env.REACT_APP_USERNAME_CLIENT ||
      !process.env.REACT_APP_PASSWORD_CLIENT
    ) {
      throw new Error("Required environment variables are missing");
    }

    // Clean up the base URL
    let baseUrl = process.env.REACT_APP_CLIENT_URL.trim();
    // Remove trailing slash if it exists
    if (baseUrl.endsWith("/")) {
      baseUrl = baseUrl.slice(0, -1);
    }
    // Remove any quotes that might be present in the URL
    baseUrl = baseUrl.replace(/"/g, "");

    // Fetch token using client credentials
    const authHeader = `Basic ${btoa(
      `${process.env.REACT_APP_USERNAME_CLIENT}:${process.env.REACT_APP_PASSWORD_CLIENT}`
    )}`;

    const tokenUrl = `${baseUrl}/am/carhire/oauth/token?grant_type=client_credentials`;

    try {
      const response = await fetch(tokenUrl, {
        method: "POST",
        headers: {
          Authorization: authHeader,
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Token fetch error response:", errorText);
        throw new Error(
          `Failed to fetch token: ${response.status} ${response.statusText}`
        );
      }

      const tokenData = await response.json();
      console.log("Token fetched successfully:", tokenData);

      // Store the token for future API calls
      localStorage.setItem("authToken", tokenData.access_token);
      return tokenData;
    } catch (error) {
      console.error("Error in fetchAuthToken:", error);
      throw error;
    }
  };

  async componentDidMount() {
    try {
      // Fetch the token first (we don't need to store it as it's saved in localStorage)
      await this.fetchAuthToken();

      // The actual order details will be set after OTP verification
      // in the handleVerify method
    } catch (error) {
      console.error("Error initializing component:", error);
      // Set error state if needed
      this.setState({
        error: "Failed to initialize. Please try again later.",
      });
    }
  }

  componentDidUpdate(prevProps: {}, prevState: GargashPageTrackerState) {
    const { isVerified, orderDetails, deliveryLocations } = this.state;

    if (isVerified && !this.state.mapInitialized) {
      this.initializeMap();
      this.setState({ mapInitialized: true });
    }

    // If delivery locations were fetched after initial map load or changed for COMPLETED status
    if (
      orderDetails?.status === "COMPLETED" &&
      JSON.stringify(deliveryLocations) !==
        JSON.stringify(prevState.deliveryLocations)
    ) {
      this.initializeMap();
    }
  }

  componentWillUnmount() {
    // Clean up routing control FIRST
    if (this.routingControl && this.map) {
      // Ensure map exists before removing control
      try {
        this.routingControl.remove(); // This calls _clearLines internally
      } catch (error) {
        console.warn("Error removing routing control during unmount:", error);
      }
      this.routingControl = null;
    }

    // Clean up map SECOND
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  fetchDeliveryLocations = async (
    deliveryPartnerId: string,
    orderNumber: string
  ): Promise<DeliveryLocation[]> => {
    try {
      const accessToken = localStorage.getItem("authToken");
      if (!accessToken) {
        throw new Error("No access token found in localStorage");
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/carhire-delivery/kirkos/delivery/delivery-location-by-order-number`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            delivery_partner_id: deliveryPartnerId,
            order_number: orderNumber,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch delivery locations");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching delivery locations:", error);
      throw error;
    }
  };

  initializeMap = async () => {
    const { orderDetails } = this.state;

    if (!orderDetails || !this.mapRef.current) return;

    // Clean up existing routing control if it exists AND the map is still active
    if (this.routingControl && this.map) {
      try {
        // @ts-ignore - The remove method exists at runtime
        this.routingControl.remove();
      } catch (error) {
        console.error("Error removing routing control:", error);
      } finally {
        this.routingControl = null;
      }
    }

    // Clean up previous map instance if exists
    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    // If status is COMPLETED, fetch delivery locations first
    if (orderDetails.status === "COMPLETED" && !this.state.deliveryLocations) {
      try {
        const deliveryLocations = await this.fetchDeliveryLocations(
          orderDetails.deliveryPartnerId,
          orderDetails.orderId
        );
        this.setState({ deliveryLocations });
      } catch (error) {
        console.error("Error fetching delivery locations:", error);
      }
      return; // Return early, componentDidUpdate will re-call initializeMap with data
    }

    // Check if map container exists and has dimensions
    if (
      this.mapRef.current.offsetWidth <= 0 ||
      this.mapRef.current.offsetHeight <= 0
    ) {
      console.error("Map container has no dimensions");
      return;
    }

    try {
      // Get driver's current location only if status is not COMPLETED
      let driverLocation: { lat: number; lng: number } | null = null;
      if (orderDetails.status !== "COMPLETED") {
        try {
          const accessToken = localStorage.getItem("authToken");
          if (!accessToken) {
            throw new Error("No access token found in localStorage");
          }
          driverLocation = await this.fetchDriverLocation(
            orderDetails.deliveryPartnerId,
            accessToken
          );
        } catch (error) {
          console.error("Could not fetch driver location:", error);
        }
      }

      // Initialize map with driver's location or pickup location as center
      const initialCenter = driverLocation || {
        lat: orderDetails.pickupLocation.lat,
        lng: orderDetails.pickupLocation.lng,
      };

      this.map = L.map(this.mapRef.current, {
        center: [initialCenter.lat, initialCenter.lng],
        zoom: 13,
        zoomControl: true,
        attributionControl: false,
      });

      // Add OpenStreetMap tile layer with error handling
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(this.map);

      // Create a feature group for bounds
      const bounds = L.latLngBounds([]);
      const markers: L.Marker[] = [];

      // Add driver's current location marker if available and status is not COMPLETED
      if (driverLocation && orderDetails.status !== "COMPLETED") {
        // Create a custom truck icon for the driver
        const driverIcon = L.divIcon({
          html: `
            <div style="
              width: 40px;
              height: 40px;
              position: relative;
              cursor: pointer;
              transform: rotate(0deg);
              transition: transform 0.3s ease;
            ">
              <svg 
                viewBox="0 0 24 24" 
                fill="#000000" 
                style="
                  width: 100%; 
                  height: 100%;
                  filter: drop-shadow(0 0 2px rgba(0,0,0,0.3));
                "
              >
                <path d="M18 18.5C18.83 18.5 19.5 17.83 19.5 17C19.5 16.17 18.83 15.5 18 15.5C17.17 15.5 16.5 16.17 16.5 17C16.5 17.83 17.17 18.5 18 18.5ZM19.5 9.5H17V12H21.46L19.5 9.5ZM6 18.5C6.83 18.5 7.5 17.83 7.5 17C7.5 16.17 6.83 15.5 6 15.5C5.17 15.5 4.5 16.17 4.5 17C4.5 17.83 5.17 18.5 6 18.5ZM20 8L23 12V17H21C21 18.66 19.66 20 18 20C16.34 20 15 18.66 15 17H9C9 18.66 7.66 20 6 20C4.34 20 3 18.66 3 17H1V6C1 4.89 1.89 4 3 4H17V8H20ZM3 6V15H3.76C4.31 14.39 5.11 14 6 14C6.89 14 7.69 14.39 8.24 15H15V6H3ZM18 14C18.89 14 19.69 14.39 20.24 15H21V12.5H19.5L18 10H17V15H17.76C17.9 14.7 18.19 14.5 18.5 14.5C18.67 14.5 18.84 14.55 19 14.6C18.95 14.4 18.74 14.25 18.5 14.25C18.26 14.25 18.05 14.4 18 14.6C18.16 14.55 18.33 14.5 18.5 14.5C18.67 14.5 18.84 14.55 19 14.6C18.95 14.4 18.74 14.25 18.5 14.25Z" />
              </svg>
            </div>
          `,
          className: "driver-location-marker",
          iconSize: [40, 40],
          iconAnchor: [20, 20],
          popupAnchor: [0, -20],
          tooltipAnchor: [0, -20],
        });

        const driverMarker = L.marker(
          [driverLocation.lat, driverLocation.lng],
          {
            icon: driverIcon,
          }
        );

        // Store coordinates in local constant to avoid closure issues
        const driverCoords = { ...driverLocation };

        // Create a function to update the popup content
        const updatePopupContent = async () => {
          try {
            const address = await this.getAddressFromCoordinates(
              driverCoords.lat,
              driverCoords.lng
            );
            // Update the state with the address for display in the details section
            this.setState({ driverLocationAddress: address });
            return `<b>Driver Location</b><br>${address}`;
          } catch (error) {
            console.error("Error fetching address:", error);
            return "<b>Driver Location</b><br>Unable to load address";
          }
        };

        // Initialize popup with loading message
        driverMarker.bindPopup("<b>Driver Location</b><br>Loading address...");

        // Add to map first
        driverMarker.addTo(this.map);

        // Function to handle marker click and update popup
        const handleMarkerClick = async () => {
          // Set loading message first
          driverMarker.setPopupContent(
            "<b>Driver Location</b><br>Loading address..."
          );

          // Open the popup first to ensure it's visible
          driverMarker.openPopup();

          try {
            // Update the content
            const popupContent = await updatePopupContent();
            driverMarker.setPopupContent(popupContent);

            // Reopen with new content if needed
            if (!driverMarker.isPopupOpen()) {
              driverMarker.openPopup();
            }
          } catch (error) {
            console.error("Error updating popup:", error);
            driverMarker.setPopupContent(
              "<b>Driver Location</b><br>Error loading address"
            );
          }
        };

        // Set up click handler
        driverMarker.on("click", handleMarkerClick);

        // Initial load of the address
        handleMarkerClick();

        markers.push(driverMarker);
        bounds.extend([driverLocation.lat, driverLocation.lng]);
      }

      // Add pickup location marker (which is the delivery_location for LOCATION_BASED)
      // Skip adding pickup marker if serviceType is LOCATION_BASED and status is COMPLETED
      if (
        !(
          orderDetails.serviceType === "LOCATION_BASED" &&
          orderDetails.status === "COMPLETED"
        )
      ) {
        markers.push(
          L.marker(
            [orderDetails.pickupLocation.lat, orderDetails.pickupLocation.lng],
            { icon: DefaultIcon }
          )
            .bindPopup(`<b>Pickup:</b> ${orderDetails.pickupLocation.address}`)
            .addTo(this.map)
        );
        bounds.extend([
          orderDetails.pickupLocation.lat,
          orderDetails.pickupLocation.lng,
        ]);
      }

      // Add drop location marker ONLY if:
      // 1. serviceType is DISTANCE_BASED, or
      // 2. It's LOCATION_BASED and COMPLETED but not both
      if (
        orderDetails.serviceType === "DISTANCE_BASED" ||
        (orderDetails.serviceType === "LOCATION_BASED" &&
          orderDetails.status !== "COMPLETED")
      ) {
        markers.push(
          L.marker(
            [orderDetails.dropLocation.lat, orderDetails.dropLocation.lng],
            { icon: DefaultIcon }
          )
            .bindPopup(`<b>Drop:</b> ${orderDetails.dropLocation.address}`)
            .addTo(this.map)
        );
        bounds.extend([
          orderDetails.dropLocation.lat,
          orderDetails.dropLocation.lng,
        ]);
      }

      // Fit map to show all markers with padding
      if (bounds.isValid()) {
        this.map.fitBounds(bounds.pad(0.2));
      }

      // Set up routing based on service type and status
      let waypoints: L.LatLng[] = [];
      let startPointForRoute: L.LatLng | null = null; // Can be pickup or driver location
      let endPointForRoute: L.LatLng | null = null; // This will be the destination point for the route

      // Logic for COMPLETED status using fetched deliveryLocations
      if (
        orderDetails.status === "COMPLETED" &&
        this.state.deliveryLocations?.length
      ) {
        const locations = this.state.deliveryLocations;

        if (orderDetails.serviceType === "DISTANCE_BASED") {
          // For DISTANCE_BASED: Find REACHED_CUSTOMER_LOCATION as start, and COMPLETED as end
          const reachedLocation = locations.find(
            (loc) => loc.delivery_partner_status === "REACHED_CUSTOMER_LOCATION"
          );
          const completedLocation = locations.find(
            (loc) => loc.delivery_partner_status === "COMPLETED"
          );

          if (reachedLocation && completedLocation) {
            startPointForRoute = L.latLng(
              parseFloat(reachedLocation.latitude),
              parseFloat(reachedLocation.longitude)
            );
            endPointForRoute = L.latLng(
              parseFloat(completedLocation.latitude),
              parseFloat(completedLocation.longitude)
            );
          }
        } else if (orderDetails.serviceType === "LOCATION_BASED") {
          // For LOCATION_BASED with COMPLETED status: Find ASSIGNED as start, and COMPLETED as end
          const assignedLocation = locations.find(
            (loc) => loc.delivery_partner_status === "ASSIGNED"
          );
          const completedLocation = locations.find(
            (loc) => loc.delivery_partner_status === "COMPLETED"
          );

          if (assignedLocation && completedLocation) {
            // Add start point marker (ASSIGNED location)
            const startMarker = L.marker(
              [
                parseFloat(assignedLocation.latitude),
                parseFloat(assignedLocation.longitude),
              ],
              { icon: DefaultIcon }
            ).bindPopup(`
              <div style="min-width: 200px">
                <div style="font-weight: bold; margin-bottom: 5px;">Start Location</div>
                <div>${assignedLocation.location || "Assigned Location"}</div>
                <div style="margin-top: 5px; font-size: 0.9em; color: #666;">
                  Status: ${assignedLocation.delivery_partner_status}<br>
                  ${new Date(assignedLocation.changed_at).toLocaleString()}
                </div>
              </div>
            `);
            markers.push(startMarker);
            bounds.extend([
              parseFloat(assignedLocation.latitude),
              parseFloat(assignedLocation.longitude),
            ]);

            // Add end point marker (COMPLETED location) - using DefaultIcon to match ASSIGNED marker
            const endMarker = L.marker(
              [
                parseFloat(completedLocation.latitude),
                parseFloat(completedLocation.longitude),
              ],
              { icon: DefaultIcon }
            ).bindPopup(`
              <div style="min-width: 200px">
                <div style="font-weight: bold; margin-bottom: 5px; color: #10b981;">Completed Location</div>
                <div>${completedLocation.location || "Completed Location"}</div>
                <div style="margin-top: 5px; font-size: 0.9em; color: #666;">
                  Status: ${completedLocation.delivery_partner_status}<br>
                  ${new Date(completedLocation.changed_at).toLocaleString()}
                </div>
              </div>
            `);
            markers.push(endMarker);
            bounds.extend([
              parseFloat(completedLocation.latitude),
              parseFloat(completedLocation.longitude),
            ]);

            // Set route points
            startPointForRoute = L.latLng(
              parseFloat(assignedLocation.latitude),
              parseFloat(assignedLocation.longitude)
            );
            endPointForRoute = L.latLng(
              parseFloat(completedLocation.latitude),
              parseFloat(completedLocation.longitude)
            );

            // Add markers to map
            markers.forEach((marker) => marker.addTo(this.map!));
          }
        }
      } else {
        // Logic for statuses other than COMPLETED
        if (orderDetails.serviceType === "DISTANCE_BASED") {
          const statusesForPickupToDrop = [
            "ASSIGNED",
            "DELIVERY_ACCEPTED",
            "DELIVERY_STARTED",
          ];

          if (driverLocation) {
            if (
              statusesForPickupToDrop.includes(
                orderDetails.deliveryPartnerStatus
              )
            ) {
              // For these statuses, create a route from driver's current location to pickup and then to drop
              waypoints = [
                L.latLng(driverLocation.lat, driverLocation.lng),
                L.latLng(
                  orderDetails.pickupLocation.lat,
                  orderDetails.pickupLocation.lng
                ),
                L.latLng(
                  orderDetails.dropLocation.lat,
                  orderDetails.dropLocation.lng
                ),
              ];
              startPointForRoute = waypoints[0];
              endPointForRoute = waypoints[2];
            } else {
              // For other statuses, just show from driver's location to drop location
              startPointForRoute = L.latLng(
                driverLocation.lat,
                driverLocation.lng
              );
              endPointForRoute = L.latLng(
                orderDetails.dropLocation.lat,
                orderDetails.dropLocation.lng
              );
            }
          } else if (
            statusesForPickupToDrop.includes(orderDetails.deliveryPartnerStatus)
          ) {
            // If no driver location but status requires pickup to drop, show that
            startPointForRoute = L.latLng(
              orderDetails.pickupLocation.lat,
              orderDetails.pickupLocation.lng
            );
            endPointForRoute = L.latLng(
              orderDetails.dropLocation.lat,
              orderDetails.dropLocation.lng
            );
          }
        } else if (orderDetails.serviceType === "LOCATION_BASED") {
          // For LOCATION_BASED (when not COMPLETED): Draw from driver's current location to pickupLocation
          if (driverLocation) {
            startPointForRoute = L.latLng(
              driverLocation.lat,
              driverLocation.lng
            );
            endPointForRoute = L.latLng(
              orderDetails.pickupLocation.lat, // Use pickupLocation as the "to location"
              orderDetails.pickupLocation.lng
            );
          } else {
            // Fallback: If no driver location, use pickup location as start (or handle as error/no route)
            console.warn(
              "Driver location not available for LOCATION_BASED service. Cannot draw route."
            );
            startPointForRoute = null; // No route can be drawn without driver location
            endPointForRoute = null;
          }
        }
      }

      // Add waypoints for routing if start and end points are determined
      if (startPointForRoute && endPointForRoute) {
        waypoints.push(startPointForRoute);
        // Only add endPoint if it's different from startPoint, to avoid single-point route
        if (!startPointForRoute.equals(endPointForRoute)) {
          waypoints.push(endPointForRoute);
        }
      } else {
        console.warn(
          "Could not determine valid start and end points for routing. Route will not be drawn."
        );
      }

      // Add routing control if we have at least 2 waypoints
      if (waypoints.length >= 2 && this.map) {
        // Ensure this.map exists before adding routing control
        try {
          // @ts-ignore - The type definitions might be outdated
          this.routingControl = L.Routing.control({
            waypoints: waypoints,
            routeWhileDragging: false,
            showAlternatives: false, // Hide alternative routes
            lineOptions: {
              styles: [
                {
                  color:
                    orderDetails.status === "COMPLETED" ? "#10b981" : "#3b82f6",
                  opacity: 0.7,
                  weight: 5,
                },
              ],
              extendToWaypoints: true,
              missingRouteTolerance: 10,
              addWaypoints: false,
              missingRouteStyles: [{ color: "red", opacity: 0.7, weight: 3 }],
            },
            // @ts-ignore - The type definitions might be outdated
            createMarker: (i: number, wp: any, n: number) => {
              // Hide intermediate markers for cleaner UI
              if (i > 0 && i < n - 1) return null;

              // If LOCATION_BASED and not COMPLETED, the last marker is the pickup location
              if (
                orderDetails.serviceType === "LOCATION_BASED" &&
                orderDetails.status !== "COMPLETED" &&
                i === n - 1
              ) {
                const blueIcon = L.divIcon({
                  html: '<div style="background-color: #3b82f6; width: 16px; height: 16px; border-radius: 50%; border: 2px solid white;"></div>',
                  className: "",
                  iconSize: [20, 20],
                  iconAnchor: [10, 10],
                });

                const marker = L.marker(wp.latLng, { icon: blueIcon });
                if (this.state.orderDetails) {
                  marker.bindPopup(
                    `<b>To Location:</b> ${this.state.orderDetails.pickupLocation.address}`
                  );
                }
                return marker;
              }

              // Create a red icon for the dropoff location (last waypoint) for DISTANCE_BASED or COMPLETED LOCATION_BASED
              if (i === n - 1) {
                const redIcon = L.divIcon({
                  html: '<div style="background-color: #ef4444; width: 18px; height: 18px; border-radius: 50%; border: 2px solid white; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold;">D</div>',
                  className: "dropoff-marker",
                  iconSize: [22, 22],
                  iconAnchor: [11, 11],
                });

                const marker = L.marker(wp.latLng, { icon: redIcon });

                // Add popup with location name if available
                if (this.state.orderDetails) {
                  marker.bindPopup(
                    `<b>Dropoff:</b> ${this.state.orderDetails.dropLocation.address}`
                  );
                }

                return marker;
              }

              // Default blue icon for pickup/first point
              return L.marker(wp.latLng, {
                icon: L.divIcon({
                  html: '<div style="background-color: #3b82f6; width: 16px; height: 16px; border-radius: 50%; border: 2px solid white;"></div>',
                  className: "",
                  iconSize: [20, 20],
                  iconAnchor: [10, 10],
                }),
              });
            },
            addWaypoints: false,
            draggableWaypoints: false,
            show: false, // Hide the directions panel
            fitSelectedRoutes: true,
          }).addTo(this.map);

          // Force a resize event to render the map properly
          setTimeout(() => {
            if (this.map) {
              this.map.invalidateSize();
            }
          }, 100);
        } catch (error) {
          console.error("Error initializing routing control:", error);
          this.routingControl = null;
        }
      }
    } catch (error) {
      console.error("Error initializing map:", error);
    }
  };

  handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^\d{0,6}$/.test(value)) {
      this.setState({ otp: value });
    }
  };

  fetchDriverLocation = async (
    deliveryPartnerId: string,
    accessToken: string
  ) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/carhire-delivery/kirkos/delivery/delivery-partner-location/${deliveryPartnerId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch driver location");
      }

      const data = await response.json();
      return {
        lat: parseFloat(data.latitude) || 0,
        lng: parseFloat(data.longitude) || 0,
      };
    } catch (error) {
      console.error("Error fetching driver location:", error);
      throw error;
    }
  };

  fetchOrderDetails = async (orderNumber: string, accessToken: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/carhire-order/order/service/backoffice/${orderNumber}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
            location: "TVM",
            language: "engy",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch order details");
      }

      const data = await response.json();

      // Map the API response to the expected OrderDetails format
      return {
        orderId: data.orderId,
        customerName: data.aaa_service_request_model?.customerName || "N/A",
        vehicle: data.order_service_details?.serviceName || "N/A",
        driverName:
          data.delivery_boy_details?.firstName +
            data.delivery_boy_details?.lastName || "N/A",
        driverNumber: data.delivery_boy_details?.phoneNumber || "N/A",
        plateNumber: data.plate_number || "N/A",
        chassisNumber: data.chassis_number || "N/A",
        serviceDate: data.aaa_service_request_model?.serviceDate || "N/A",
        status: data.orderStatus || "N/A",
        deliveryPartnerStatus: data.deliveryPartnerStatus || "N/A",
        amount: 0, // Not present in the response, set default
        pickupLocation: {
          lat: parseFloat(data.delivery_location?.latitude) || 0,
          lng: parseFloat(data.delivery_location?.longitude) || 0,
          address: data.delivery_location?.address || "N/A",
        },
        dropLocation: {
          lat: parseFloat(data.recollection_location?.latitude) || 0,
          lng: parseFloat(data.recollection_location?.longitude) || 0,
          address: data.recollection_location?.address || "N/A",
        },
        deliveryPartnerId: data.deliveryPartnerId || "N/A",
        serviceType: data.order_service_details?.serviceType || "N/A",
      };
    } catch (error) {
      console.error("Error fetching order details:", error);
      throw error;
    }
  };

  verifyOtp = async (otp: string) => {
    try {
      // First get the auth token
      const tokenData = await this.fetchAuthToken();
      const accessToken = tokenData.access_token;

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/carhire-order/order/service/merchants/verify-otp?otp=${otp}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to verify OTP");
      }

      const result = await response.json();

      // If OTP verification is successful, fetch order details
      if (result.success && result.orderNumber) {
        const orderDetails = await this.fetchOrderDetails(
          result.orderNumber,
          accessToken
        );
        return { ...result, orderDetails };
      }

      return result;
    } catch (error) {
      console.error("Error in verifyOtp:", error);
      throw error;
    }
  };

  handleVerify = async () => {
    const { otp } = this.state;

    if (otp.length !== 6) {
      alert("Please enter a valid 6-digit OTP");
      return;
    }

    try {
      this.setState({ isLoading: true, error: undefined }); // Set loading state
      const result = await this.verifyOtp(otp);

      // If we have order details, update the state with them
      if (result.orderDetails) {
        this.setState({
          showOtpModal: false,
          isVerified: true,
          orderDetails: result.orderDetails, // Assuming the response structure matches your OrderDetails type
          isLoading: false,
        });
      } else {
        this.setState({
          showOtpModal: false,
          isVerified: true,
          isLoading: false,
        });
      }
    } catch (error) {
      // Handle error - show error message to user
      this.setState({
        error: error instanceof Error ? error.message : "Failed to verify OTP",
        isLoading: false,
      });
      alert(this.state.error); // Display error using alert
    }
  };

  render() {
    const { showOtpModal, otp, isVerified, orderDetails, isLoading, error } =
      this.state;

    return (
      <div className="gargash-container">
        <Modal
          title={
            <Title level={4} className="otp-modal-title">
              Verify Your Identity
            </Title>
          }
          open={showOtpModal} // Use 'open' instead of 'visible' for Ant Design v5+
          footer={null}
          closable={false}
          centered
        >
          <div className="text-center mb-4">
            <Text className="otp-description">
              Please enter the 6-digit OTP sent to your registered mobile number
            </Text>
            <div className="my-4">
              <Input
                size="large"
                className="otp-input"
                maxLength={6}
                value={otp}
                onChange={this.handleOtpChange}
                placeholder="_ _ _ _ _ _"
                disabled={isLoading}
              />
            </div>
            {error && <Text type="danger">{error}</Text>}{" "}
            {/* Display error message */}
            <Button
              type="primary"
              className="verify-button"
              onClick={this.handleVerify}
              disabled={otp.length !== 6 || isLoading}
              loading={isLoading}
            >
              Verify OTP
            </Button>
          </div>
        </Modal>

        {isVerified && orderDetails && (
          <div className="order-details-container">
            <div className="success-message">
              <CheckCircleOutlined className="success-icon" />
              <span className="success-title">Order Verified Successfully</span>
            </div>

            <Card className="order-details-card" bordered={false}>
              <Row gutter={[24, 24]} className="details-map-inner-container">
                <Col xs={24} md={12}>
                  <Title level={4} className="section-title">
                    Order Information
                  </Title>
                  <Divider className="section-divider" />
                  <div className="details-grid">
                    <div className="details-item">
                      <div className="details-label-wrapper">
                        <UserOutlined className="details-icon" />
                        <Text strong className="order-details-label">
                          Customer Name:
                        </Text>
                      </div>
                      <Text className="order-details-value">
                        {orderDetails.customerName}
                      </Text>
                    </div>
                    <div className="details-item">
                      <div className="details-label-wrapper">
                        <CarOutlined className="details-icon" />
                        <Text strong className="order-details-label">
                          Driver Name & No:
                        </Text>
                      </div>
                      <Text className="order-details-value">
                        {orderDetails.driverName} & {orderDetails.driverNumber}
                      </Text>
                    </div>
                    <div className="details-item">
                      <div className="details-label-wrapper">
                        <CarOutlined className="details-icon" />
                        <Text strong className="order-details-label">
                          Plate & Chassis No:
                        </Text>
                      </div>
                      <Text className="order-details-value">
                        {orderDetails.plateNumber} &{" "}
                        {orderDetails.chassisNumber}
                      </Text>
                    </div>
                    <div className="details-item">
                      <div className="details-label-wrapper">
                        <CalendarOutlined className="details-icon" />
                        <Text strong className="order-details-label">
                          Service Date:
                        </Text>
                      </div>
                      <Text className="order-details-value">
                        {this.formatDate(orderDetails.serviceDate)}
                      </Text>
                    </div>
                    <div className="details-item">
                      <div className="details-label-wrapper">
                        <SolutionOutlined className="details-icon" />
                        <Text strong className="order-details-label">
                          Status:
                        </Text>
                      </div>
                      <span className="status-badge status-in-progress">
                        {orderDetails.deliveryPartnerStatus}
                      </span>
                    </div>
                    {orderDetails.serviceType === "DISTANCE_BASED" ? (
                      <>
                        <div className="details-item">
                          <div className="details-label-wrapper">
                            <HomeOutlined className="details-icon" />
                            <Text strong className="order-details-label">
                              Pickup Location:
                            </Text>
                          </div>
                          <Text className="order-details-value">
                            {orderDetails.pickupLocation.address}
                          </Text>
                        </div>
                        <div className="details-item">
                          <div className="details-label-wrapper">
                            <EnvironmentOutlined className="details-icon" />
                            <Text strong className="order-details-label">
                              Drop Location:
                            </Text>
                          </div>
                          <Text className="order-details-value">
                            {orderDetails.dropLocation.address}
                          </Text>
                        </div>
                      </>
                    ) : (
                      <div className="details-item">
                        <div className="details-label-wrapper">
                          <EnvironmentOutlined className="details-icon" />
                          <Text strong className="order-details-label">
                            To Location:
                          </Text>
                        </div>
                        <Text className="order-details-value">
                          {orderDetails.pickupLocation.address}
                        </Text>
                      </div>
                    )}
                  </div>
                </Col>

                <Col xs={24} md={12}>
                  <Title level={4} className="section-title">
                    Live Tracking
                  </Title>
                  <Divider className="section-divider" />
                  <div className="map-container" ref={this.mapRef}>
                    <div className="loading-map">Loading map...</div>
                  </div>
                  {this.state.orderDetails?.status !== "COMPLETED" &&
                    this.state.driverLocationAddress && (
                      <div className="details-item">
                        <div className="details-label-wrapper">
                          <CarOutlined className="details-icon" />
                          <Text strong className="order-details-label">
                            Driver Current Location:
                          </Text>
                        </div>
                        <Text className="order-details-value">
                          {this.state.driverLocationAddress}
                        </Text>
                      </div>
                    )}
                </Col>
              </Row>
            </Card>
          </div>
        )}
      </div>
    );
  }
}

export default GargashPageTracker;
